import { compactPromotionPaymentFields } from "../supabase/edge-functions/_shared/promotionPayments";
import { commissionBaseBeforeLocalDiscount } from "../supabase/edge-functions/_shared/commissionPricing";
import {
  DEFAULT_BRANCH_COMMISSION_RATE,
  normalizeCommissionRate,
} from "./paymentSettings";
import {
  buildPaymentRailSnapshot,
  COMMISSION_COLLECTION_MODES,
  getPaymentRailLabel,
  isCounterPaymentRail,
  isDigitalPaymentRail,
  normalizePaymentRail,
} from "../payments/paymentRails";

const STORAGE_PREFIX = "mozzoapp:payment-operations:";
const SESSION_STORAGE_PREFIX = "mozzoapp:payment-operations-session:";
const inMemoryPaymentOperationsStore = new Map();
export const PAYMENT_OPERATIONS_UPDATED_EVENT =
  "mozzoapp:payment-operations-updated";
export const PAYMENT_OPERATIONS_REMOTE_SYNC_SOURCE = "remote-sync";
export const PAYMENT_PLATFORM_FEE_RATE = DEFAULT_BRANCH_COMMISSION_RATE;

const SETTLED_STATUSES = new Set(["paid", "closed"]);
const CONFIRMED_PAYMENT_STATUSES = new Set([
  "paid",
  "approved",
  "accredited",
  "settled",
  "completed",
  "closed",
]);
const PENDING_PAYMENT_STATUSES = new Set([
  "created",
  "link_ready",
  "pending",
  "pending_confirmation",
  "link_sent",
]);
const CANCELLED_PAYMENT_STATUSES = new Set([
  "cancelled",
  "canceled",
  "rejected",
  "failed",
  "expired",
  "voided",
]);
const LOCAL_COLLECTION_METHODS = new Set([
  "counter",
  "pay_at_counter",
  "caja",
  "efectivo",
  "cash",
  "transferencia",
  "transfer",
  "tarjeta",
  "card",
  "wallet",
  "mixed",
]);

export const buildPaymentOperationsStorageKey = (restaurantId) =>
  `${STORAGE_PREFIX}${restaurantId || "default"}`;

const buildPaymentOperationsSessionStorageKey = (restaurantId) =>
  `${SESSION_STORAGE_PREFIX}${restaurantId || "default"}`;
const createEmptyPaymentOperations = () => [];

const toSafeText = (value, maxLength = 240) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toSafeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value) => Math.round(toSafeNumber(value) * 100) / 100;

const resolveCommissionRate = (operation) =>
  normalizeCommissionRate(
    operation?.commissionRate ??
      operation?.economics?.platformFeeRate ??
      PAYMENT_PLATFORM_FEE_RATE
  );

const resolvePaymentMethod = (operation) =>
  String(operation?.selectedMethod || operation?.requestedMethod || "")
    .trim()
    .toLowerCase();

const isSettledOperation = (operation) =>
  SETTLED_STATUSES.has(String(operation?.status || "").trim().toLowerCase());

const resolvePaymentRail = (operation) => {
  const derivedRail = buildPaymentRailSnapshot({
    source: operation?.source,
    checkoutMethod: resolvePaymentMethod(operation),
    paymentProvider: operation?.paymentRailProvider || operation?.provider || "",
    splitMode: operation?.paymentSplitMode,
    isKioskExperience: operation?.source === "kiosk",
  }).rail;

  return normalizePaymentRail(operation?.paymentRail || derivedRail);
};

const resolveCommissionCollectionMode = (operation) =>
  toSafeText(
    operation?.commissionCollectionMode ||
      buildPaymentRailSnapshot({
        source: operation?.source,
        checkoutMethod: resolvePaymentMethod(operation),
        paymentProvider: operation?.paymentRailProvider || operation?.provider || "",
        splitMode: operation?.paymentSplitMode,
        isKioskExperience: operation?.source === "kiosk",
      }).commissionCollectionMode,
    60
  );

const isDigitalCollectionOperation = (operation) =>
  resolveCommissionCollectionMode(operation) ===
  COMMISSION_COLLECTION_MODES.AUTOMATIC_ON_DIGITAL_CHARGE;

const isLocalCollectionOperation = (operation) => {
  const method = resolvePaymentMethod(operation);
  const deferredDigitalCollection =
    isDigitalPaymentRail(resolvePaymentRail(operation)) &&
    resolveCommissionCollectionMode(operation) ===
      COMMISSION_COLLECTION_MODES.DEFERRED_TO_NEXT_DIGITAL;
  return isCounterPaymentRail(resolvePaymentRail(operation))
    ? true
    : deferredDigitalCollection
    ? true
    : method
      ? LOCAL_COLLECTION_METHODS.has(method)
      : false;
};

const getFinancialTimestamp = (operation) =>
  operation?.paidAt || operation?.closedAt || operation?.orderedAt || "";

const compactPricingSnapshot = (pricing) => {
  if (!pricing || typeof pricing !== "object") {
    return null;
  }

  return {
    ...compactPromotionPaymentFields(pricing),
    listTotal: Number(pricing.listTotal || 0),
    comboDiscountTotal: Number(pricing.comboDiscountTotal || 0),
    promotionDiscountTotal: Number(pricing.promotionDiscountTotal || 0),
    totalDiscount: Number(pricing.totalDiscount || 0),
    finalTotal: Number(pricing.finalTotal || 0),
    adjustments: Array.isArray(pricing.adjustments)
      ? pricing.adjustments.slice(0, 8).map((adjustment) => ({
          id: adjustment?.id || "",
          type: adjustment?.type || "",
          title: toSafeText(adjustment?.title, 120),
          amount: Number(adjustment?.amount || 0),
          summary: toSafeText(adjustment?.summary, 140),
        }))
      : [],
  };
};

const compactEconomicsSnapshot = (economics) => {
  if (!economics || typeof economics !== "object") {
    return null;
  }

  return {
    grossAmount: roundMoney(economics.grossAmount),
    commissionBaseAmount: roundMoney(economics.commissionBaseAmount ?? economics.grossAmount),
    platformFeeRate: Number(economics.platformFeeRate ?? PAYMENT_PLATFORM_FEE_RATE),
    platformFeeAmount: roundMoney(economics.platformFeeAmount),
    merchantNetAmount: roundMoney(economics.merchantNetAmount),
    payoutAmount:
      economics.payoutAmount === null || economics.payoutAmount === undefined
        ? null
        : roundMoney(economics.payoutAmount),
    retainedAmount: roundMoney(economics.retainedAmount),
    retainedCurrentFeeAmount: roundMoney(economics.retainedCurrentFeeAmount),
    retainedPastDebtAmount: roundMoney(economics.retainedPastDebtAmount),
    generatedDebtAmount: roundMoney(economics.generatedDebtAmount),
    pendingDebtAmount: roundMoney(economics.pendingDebtAmount),
    outstandingDebtAfterOperation: roundMoney(economics.outstandingDebtAfterOperation),
    settlementMode: toSafeText(economics.settlementMode, 40),
    commissionCollectionMode: toSafeText(
      economics.commissionCollectionMode,
      60
    ),
    isSettled: economics.isSettled === true,
    settlementSources: Array.isArray(economics.settlementSources)
      ? economics.settlementSources.slice(0, 16).map((entry) => ({
          operationId: entry?.operationId || "",
          orderId: entry?.orderId || "",
          mesa: toSafeText(entry?.mesa, 120),
          amount: roundMoney(entry?.amount),
        }))
      : [],
    settlementTargets: Array.isArray(economics.settlementTargets)
      ? economics.settlementTargets.slice(0, 16).map((entry) => ({
          operationId: entry?.operationId || "",
          orderId: entry?.orderId || "",
          mesa: toSafeText(entry?.mesa, 120),
          amount: roundMoney(entry?.amount),
        }))
      : [],
  };
};

const normalizePaymentOperation = (operation) => {
  const grossAmount = roundMoney(operation?.grossAmount ?? operation?.amount ?? 0);
  const paymentRailSnapshot = buildPaymentRailSnapshot({
    source: operation?.source,
    checkoutMethod: resolvePaymentMethod(operation),
    paymentProvider: operation?.paymentRailProvider || operation?.provider || "",
    splitMode: operation?.paymentSplitMode,
    isKioskExperience: operation?.source === "kiosk",
  });
  const paymentRail = normalizePaymentRail(
    operation?.paymentRail || paymentRailSnapshot.rail
  );

  return {
    ...operation,
    id: operation?.id || "",
    orderId: operation?.orderId || "",
    tableId: operation?.tableId || "",
    mesa: toSafeText(operation?.mesa, 120),
    customerName: toSafeText(operation?.customerName, 80),
    customerSessionId: toSafeText(operation?.customerSessionId, 120),
    canal: toSafeText(operation?.canal, 60),
    source: toSafeText(operation?.source, 40),
    requestedMethod: toSafeText(operation?.requestedMethod, 40),
    selectedMethod: toSafeText(operation?.selectedMethod, 40),
    paymentRail,
    paymentRailProvider: toSafeText(
      operation?.paymentRailProvider || paymentRailSnapshot.provider,
      40
    ),
    paymentRailLabel: toSafeText(
      operation?.paymentRailLabel || getPaymentRailLabel(paymentRail),
      80
    ),
    chargePresentation: toSafeText(
      operation?.chargePresentation || paymentRailSnapshot.chargePresentation,
      40
    ),
    commissionCollectionMode: toSafeText(
      operation?.commissionCollectionMode ||
        paymentRailSnapshot.commissionCollectionMode,
      60
    ),
    splitExecutionMode: toSafeText(
      operation?.splitExecutionMode || paymentRailSnapshot.splitExecutionMode,
      60
    ),
    interoperabilityObjective: toSafeText(
      operation?.interoperabilityObjective ||
        paymentRailSnapshot.interoperabilityObjective,
      80
    ),
    paymentSplitMode: toSafeText(
      operation?.paymentSplitMode || paymentRailSnapshot.splitMode,
      60
    ),
    status: toSafeText(operation?.status, 30),
    amount: grossAmount,
    grossAmount,
    commissionRate: resolveCommissionRate(operation),
    pricing: compactPricingSnapshot(operation?.pricing),
    orderedAt: operation?.orderedAt || "",
    paidAt: operation?.paidAt || null,
    closedAt: operation?.closedAt || null,
    itemsSummary: toSafeText(operation?.itemsSummary, 260),
    notes: toSafeText(operation?.notes, 260),
    history: Array.isArray(operation?.history)
      ? operation.history.slice(-12).map((entry) => ({
          id: entry?.id || "",
          label: toSafeText(entry?.label, 180),
          at: entry?.at || "",
        }))
      : [],
    economics: compactEconomicsSnapshot(operation?.economics),
  };
};

const buildBaseEconomics = (operation) => {
  const grossAmount = roundMoney(operation?.grossAmount ?? operation?.amount);
  const commissionRate = resolveCommissionRate(operation);
  const commissionBaseAmount = commissionBaseBeforeLocalDiscount(operation?.pricing, grossAmount);
  const platformFeeAmount = roundMoney(commissionBaseAmount * commissionRate);
  const commissionCollectionMode = resolveCommissionCollectionMode(operation);

  return {
    grossAmount,
    commissionBaseAmount,
    platformFeeRate: commissionRate,
    platformFeeAmount,
    merchantNetAmount: roundMoney(grossAmount - platformFeeAmount),
    payoutAmount: null,
    retainedAmount: 0,
    retainedCurrentFeeAmount: 0,
    retainedPastDebtAmount: 0,
    generatedDebtAmount: 0,
    pendingDebtAmount: 0,
    outstandingDebtAfterOperation: 0,
    settlementMode: isSettledOperation(operation)
      ? isDigitalCollectionOperation(operation)
        ? "digital_retention"
        : isDigitalPaymentRail(resolvePaymentRail(operation))
          ? "digital_deferred"
        : "local_collection"
      : "pending",
    commissionCollectionMode,
    isSettled: isSettledOperation(operation),
    settlementSources: [],
    settlementTargets: [],
  };
};

const sumOutstandingDebt = (queue) =>
  roundMoney(queue.reduce((sum, entry) => sum + roundMoney(entry?.remainingAmount), 0));

const sortOperationsForSettlement = (operations) =>
  [...operations].sort((left, right) => {
    const leftTime = new Date(getFinancialTimestamp(left) || 0).getTime();
    const rightTime = new Date(getFinancialTimestamp(right) || 0).getTime();

    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }

    return String(left.id || "").localeCompare(String(right.id || ""));
  });

const getOperationRecencyTime = (operation) => {
  const candidates = [
    operation?.updatedAt,
    operation?.closedAt,
    operation?.paidAt,
    operation?.orderedAt,
  ];
  const timestamps = candidates
    .map((value) => new Date(value || 0).getTime())
    .filter((value) => Number.isFinite(value) && value > 0);

  return timestamps.length > 0 ? Math.max(...timestamps) : 0;
};

const normalizePaymentStatus = (value) =>
  String(value || "").trim().toLowerCase();

export const isPaymentStatusConfirmed = (status) =>
  CONFIRMED_PAYMENT_STATUSES.has(normalizePaymentStatus(status));

export const isPaymentOperationConfirmed = (operation) => {
  const status = normalizePaymentStatus(operation?.status);

  if (CANCELLED_PAYMENT_STATUSES.has(status)) {
    return false;
  }

  return isPaymentStatusConfirmed(status) || Boolean(operation?.paidAt);
};

export const isOperationalOrderPaymentConfirmed = (order) => {
  if (!order || typeof order !== "object") {
    return false;
  }

  return (
    Boolean(order.paidAt || order.cashCollectedAt) ||
    isPaymentStatusConfirmed(
      order.paymentStatus ||
        order.payment_status ||
        order.paymentProviderStatus ||
        order.payment_provider_status
    )
  );
};

export const getPaymentOperationsForOrder = (operations, orderId) => {
  const safeOrderId = String(orderId || "").trim();

  if (!safeOrderId) {
    return [];
  }

  return (Array.isArray(operations) ? operations : []).filter(
    (operation) => String(operation?.orderId || "").trim() === safeOrderId
  );
};

const sortPaymentOperationsByRecency = (operations) =>
  [...operations].sort((left, right) => {
    const rightTime = getOperationRecencyTime(right);
    const leftTime = getOperationRecencyTime(left);

    if (rightTime !== leftTime) {
      return rightTime - leftTime;
    }

    return String(right?.id || "").localeCompare(String(left?.id || ""));
  });

export const isPaymentOperationPendingDigital = (operation) => {
  const status = normalizePaymentStatus(operation?.status);

  if (
    isPaymentOperationConfirmed(operation) ||
    CANCELLED_PAYMENT_STATUSES.has(status) ||
    (!PENDING_PAYMENT_STATUSES.has(status) && !operation?.paymentIntentId)
  ) {
    return false;
  }

  const method = String(
    operation?.selectedMethod || operation?.requestedMethod || operation?.paymentMethod || ""
  ).toLowerCase();
  const rail = resolvePaymentRail(operation);

  return (
    isDigitalPaymentRail(rail) ||
    ["qr", "deep_link", "deeplink", "mercado", "digital", "pay_by_phone"].some(
      (token) => method.includes(token)
    )
  );
};

export const getAuthoritativePaymentOperationForOrder = (
  operations,
  orderId,
  preferredOperationId = ""
) => {
  const orderOperations = getPaymentOperationsForOrder(operations, orderId);
  const confirmedOperation = sortPaymentOperationsByRecency(
    orderOperations.filter(isPaymentOperationConfirmed)
  )[0];

  if (confirmedOperation) {
    return confirmedOperation;
  }

  const pendingDigitalOperation = sortPaymentOperationsByRecency(
    orderOperations.filter(isPaymentOperationPendingDigital)
  )[0];

  if (pendingDigitalOperation) {
    return pendingDigitalOperation;
  }

  const safePreferredOperationId = String(preferredOperationId || "").trim();
  const preferredOperation = safePreferredOperationId
    ? orderOperations.find(
        (operation) => String(operation?.id || "").trim() === safePreferredOperationId
      )
    : null;

  return preferredOperation || sortPaymentOperationsByRecency(orderOperations)[0] || null;
};

// This is the single decision point used by waiter, admin and cash desk views.
// A requested digital method alone never blocks cash: only a real pending intent does.
export const resolveOperationalOrderPaymentState = (
  order,
  operations,
  preferredOperationId = ""
) => {
  const orderOperations = getPaymentOperationsForOrder(operations, order?.id);
  const confirmedOperation = sortPaymentOperationsByRecency(
    orderOperations.filter(isPaymentOperationConfirmed)
  )[0];
  const pendingDigitalOperation = sortPaymentOperationsByRecency(
    orderOperations.filter(isPaymentOperationPendingDigital)
  )[0];
  const isPaid =
    isOperationalOrderPaymentConfirmed(order) || Boolean(confirmedOperation);
  const paymentOperation =
    confirmedOperation ||
    pendingDigitalOperation ||
    getAuthoritativePaymentOperationForOrder(
      orderOperations,
      order?.id,
      preferredOperationId || order?.paymentOperationId
    );

  return {
    isPaid,
    hasPendingDigitalPayment: !isPaid && Boolean(pendingDigitalOperation),
    canCollectCash: !isPaid && !pendingDigitalOperation,
    paymentOperation,
    confirmedOperation: confirmedOperation || null,
    pendingDigitalOperation: pendingDigitalOperation || null,
  };
};

const mergeDuplicatePaymentOperation = (currentOperation, nextOperation) => {
  const currentSettled = isSettledOperation(currentOperation);
  const nextSettled = isSettledOperation(nextOperation);

  if (currentSettled !== nextSettled) {
    return nextSettled ? nextOperation : currentOperation;
  }

  const currentRecency = getOperationRecencyTime(currentOperation);
  const nextRecency = getOperationRecencyTime(nextOperation);

  if (currentRecency !== nextRecency) {
    return nextRecency > currentRecency ? nextOperation : currentOperation;
  }

  const currentHistorySize = Array.isArray(currentOperation?.history)
    ? currentOperation.history.length
    : 0;
  const nextHistorySize = Array.isArray(nextOperation?.history)
    ? nextOperation.history.length
    : 0;

  if (currentHistorySize !== nextHistorySize) {
    return nextHistorySize > currentHistorySize ? nextOperation : currentOperation;
  }

  return nextOperation;
};

const dedupePaymentOperations = (operations) => {
  const normalizedOperations = (Array.isArray(operations) ? operations : []).map(
    normalizePaymentOperation
  );
  const dedupedOperations = [];
  const operationIndexById = new Map();

  normalizedOperations.forEach((operation, index) => {
    const normalizedId = String(operation?.id || "").trim();

    if (!normalizedId) {
      dedupedOperations.push({
        ...operation,
        id: `anonymous-payment-operation-${index}`,
      });
      return;
    }

    if (!operationIndexById.has(normalizedId)) {
      operationIndexById.set(normalizedId, dedupedOperations.length);
      dedupedOperations.push(operation);
      return;
    }

    const existingIndex = operationIndexById.get(normalizedId);
    const existingOperation = dedupedOperations[existingIndex];
    const preferredOperation = mergeDuplicatePaymentOperation(
      existingOperation,
      operation
    );
    const secondaryOperation =
      preferredOperation === existingOperation ? operation : existingOperation;

    dedupedOperations[existingIndex] = normalizePaymentOperation({
      ...secondaryOperation,
      ...preferredOperation,
      history:
        Array.isArray(preferredOperation?.history) &&
        preferredOperation.history.length > 0
          ? preferredOperation.history
          : secondaryOperation?.history || [],
      pricing: preferredOperation?.pricing || secondaryOperation?.pricing || null,
      economics:
        preferredOperation?.economics || secondaryOperation?.economics || null,
    });
  });

  return dedupedOperations;
};

export const normalizePaymentOperationsCollection = (operations) => {
  const normalizedOperations = dedupePaymentOperations(operations);
  const operationsById = new Map(
    normalizedOperations.map((operation) => [
      operation.id,
      {
        ...operation,
        economics: buildBaseEconomics(operation),
      },
    ])
  );
  const debtQueue = [];

  sortOperationsForSettlement(normalizedOperations)
    .filter((operation) => operation.id)
    .forEach((operation) => {
      const currentOperation = operationsById.get(operation.id);

      if (!currentOperation || !currentOperation.economics.isSettled) {
        return;
      }

      const currentEconomics = currentOperation.economics;

      if (isDigitalCollectionOperation(currentOperation)) {
        let availableToRetain = roundMoney(currentEconomics.grossAmount);
        currentEconomics.retainedCurrentFeeAmount = roundMoney(
          Math.min(availableToRetain, currentEconomics.platformFeeAmount)
        );
        availableToRetain = roundMoney(
          availableToRetain - currentEconomics.retainedCurrentFeeAmount
        );

        while (availableToRetain > 0 && debtQueue.length > 0) {
          const debtEntry = debtQueue[0];
          const appliedAmount = roundMoney(
            Math.min(availableToRetain, roundMoney(debtEntry.remainingAmount))
          );

          if (appliedAmount <= 0) {
            debtQueue.shift();
            continue;
          }

          currentEconomics.retainedPastDebtAmount = roundMoney(
            currentEconomics.retainedPastDebtAmount + appliedAmount
          );
          currentEconomics.settlementSources.push({
            operationId: debtEntry.operationId,
            orderId: debtEntry.orderId || "",
            mesa: debtEntry.mesa || "",
            amount: appliedAmount,
          });

          const sourceOperation = operationsById.get(debtEntry.operationId);
          if (sourceOperation) {
            sourceOperation.economics.pendingDebtAmount = roundMoney(
              sourceOperation.economics.pendingDebtAmount - appliedAmount
            );
            sourceOperation.economics.settlementTargets.push({
              operationId: currentOperation.id,
              orderId: currentOperation.orderId || "",
              mesa: currentOperation.mesa || "",
              amount: appliedAmount,
            });
          }

          debtEntry.remainingAmount = roundMoney(
            roundMoney(debtEntry.remainingAmount) - appliedAmount
          );
          availableToRetain = roundMoney(availableToRetain - appliedAmount);

          if (debtEntry.remainingAmount <= 0) {
            debtQueue.shift();
          }
        }

        currentEconomics.generatedDebtAmount = roundMoney(
          currentEconomics.platformFeeAmount - currentEconomics.retainedCurrentFeeAmount
        );
        currentEconomics.pendingDebtAmount = currentEconomics.generatedDebtAmount;

        if (currentEconomics.generatedDebtAmount > 0) {
          debtQueue.push({
            operationId: currentOperation.id,
            orderId: currentOperation.orderId || "",
            mesa: currentOperation.mesa || "",
            remainingAmount: currentEconomics.generatedDebtAmount,
          });
        }

        currentEconomics.retainedAmount = roundMoney(
          currentEconomics.retainedCurrentFeeAmount +
            currentEconomics.retainedPastDebtAmount
        );
        currentEconomics.payoutAmount = roundMoney(
          currentEconomics.grossAmount - currentEconomics.retainedAmount
        );
      } else if (isLocalCollectionOperation(currentOperation)) {
        currentEconomics.generatedDebtAmount = currentEconomics.platformFeeAmount;
        currentEconomics.pendingDebtAmount = currentEconomics.platformFeeAmount;
        currentEconomics.retainedAmount = 0;
        currentEconomics.payoutAmount = currentEconomics.grossAmount;

        if (currentEconomics.generatedDebtAmount > 0) {
          debtQueue.push({
            operationId: currentOperation.id,
            orderId: currentOperation.orderId || "",
            mesa: currentOperation.mesa || "",
            remainingAmount: currentEconomics.generatedDebtAmount,
          });
        }
      } else {
        currentEconomics.payoutAmount = currentEconomics.grossAmount;
      }

      currentEconomics.outstandingDebtAfterOperation = sumOutstandingDebt(debtQueue);
    });

  const finalOutstandingDebt = sumOutstandingDebt(debtQueue);

  return normalizedOperations.map((operation) => {
    const enrichedOperation = operationsById.get(operation.id) || {
      ...operation,
      economics: buildBaseEconomics(operation),
    };

    if (!enrichedOperation.economics.isSettled) {
      enrichedOperation.economics.outstandingDebtAfterOperation = finalOutstandingDebt;
    }

    return {
      ...enrichedOperation,
      economics: compactEconomicsSnapshot(enrichedOperation.economics),
    };
  });
};

const compactPaymentOperation = (operation) => {
  if (!operation || typeof operation !== "object") {
    return operation;
  }

  return {
    ...operation,
    orderId: operation?.orderId || "",
    tableId: operation?.tableId || "",
    mesa: toSafeText(operation?.mesa, 120),
    customerName: toSafeText(operation?.customerName, 80),
    customerSessionId: toSafeText(operation?.customerSessionId, 120),
    canal: toSafeText(operation?.canal, 60),
    source: toSafeText(operation?.source, 40),
    requestedMethod: toSafeText(operation?.requestedMethod, 40),
    selectedMethod: toSafeText(operation?.selectedMethod, 40),
    status: toSafeText(operation?.status, 30),
    amount: roundMoney(operation?.grossAmount ?? operation?.amount),
    grossAmount: roundMoney(operation?.grossAmount ?? operation?.amount),
    commissionRate: resolveCommissionRate(operation),
    itemsSummary: toSafeText(operation?.itemsSummary, 260),
    notes: toSafeText(operation?.notes, 260),
    pricing: compactPricingSnapshot(operation?.pricing),
    economics: compactEconomicsSnapshot(operation?.economics),
    history: Array.isArray(operation?.history)
      ? operation.history.slice(-12).map((entry) => ({
          id: entry?.id || "",
          label: toSafeText(entry?.label, 180),
          at: entry?.at || "",
        }))
      : [],
  };
};

const buildEmergencyPaymentOperation = (operation) => ({
  id: operation?.id || "",
  orderId: operation?.orderId || "",
  tableId: operation?.tableId || "",
  mesa: toSafeText(operation?.mesa, 80),
  customerName: toSafeText(operation?.customerName, 80),
  customerSessionId: toSafeText(operation?.customerSessionId, 120),
  canal: toSafeText(operation?.canal, 40),
  source: toSafeText(operation?.source, 30),
  requestedMethod: toSafeText(operation?.requestedMethod, 30),
  selectedMethod: toSafeText(operation?.selectedMethod, 30),
  status: toSafeText(operation?.status, 30),
  amount: roundMoney(operation?.grossAmount ?? operation?.amount),
  grossAmount: roundMoney(operation?.grossAmount ?? operation?.amount),
  commissionRate: resolveCommissionRate(operation),
  pricing: compactPricingSnapshot(operation?.pricing),
  economics: compactEconomicsSnapshot(operation?.economics),
  orderedAt: operation?.orderedAt || "",
  paidAt: operation?.paidAt || null,
  closedAt: operation?.closedAt || null,
  itemsSummary: toSafeText(operation?.itemsSummary, 160),
  notes: toSafeText(operation?.notes, 160),
  history: Array.isArray(operation?.history)
    ? operation.history.slice(-6).map((entry) => ({
        id: entry?.id || "",
        label: toSafeText(entry?.label, 120),
        at: entry?.at || "",
      }))
    : [],
});

const emitPaymentOperationsUpdate = (restaurant, operations, source = "local") => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(PAYMENT_OPERATIONS_UPDATED_EVENT, {
      detail: {
        restaurantId: restaurant?.id || "default",
        operations,
        source,
      },
    })
  );
};

export const getPaymentOperations = (restaurant) => {
  const storageKey = buildPaymentOperationsStorageKey(restaurant?.id);
  const sessionStorageKey = buildPaymentOperationsSessionStorageKey(restaurant?.id);

  if (typeof window === "undefined") {
    return normalizePaymentOperationsCollection(createEmptyPaymentOperations());
  }

  if (inMemoryPaymentOperationsStore.has(storageKey)) {
    return normalizePaymentOperationsCollection(
      inMemoryPaymentOperationsStore.get(storageKey)
    );
  }

  try {
    const rawValue =
      window.sessionStorage.getItem(sessionStorageKey) ||
      window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return normalizePaymentOperationsCollection(createEmptyPaymentOperations());
    }

    const parsedValue = JSON.parse(rawValue);
    const normalizedOperations = normalizePaymentOperationsCollection(
      Array.isArray(parsedValue) ? parsedValue : createEmptyPaymentOperations()
    );
    inMemoryPaymentOperationsStore.set(storageKey, normalizedOperations);
    return normalizedOperations;
  } catch (error) {
    console.error("No pudimos leer las operaciones de pago:", error);
    return normalizePaymentOperationsCollection(
      inMemoryPaymentOperationsStore.get(storageKey) ||
        createEmptyPaymentOperations()
    );
  }
};

export const savePaymentOperations = (restaurant, operations, options = {}) => {
  const storageKey = buildPaymentOperationsStorageKey(restaurant?.id);
  const sessionStorageKey = buildPaymentOperationsSessionStorageKey(restaurant?.id);
  const payload = normalizePaymentOperationsCollection(operations).map((operation) =>
    compactPaymentOperation(operation)
  );
  const serializedPayload = JSON.stringify(payload);

  try {
    if (window.localStorage.getItem(storageKey) === serializedPayload) {
      return payload;
    }

    window.localStorage.setItem(storageKey, serializedPayload);
    inMemoryPaymentOperationsStore.set(storageKey, payload);
    try {
      window.sessionStorage.setItem(sessionStorageKey, serializedPayload);
    } catch (sessionMirrorError) {
      console.warn(
        "No pudimos espejar las operaciones de pago en sessionStorage:",
        sessionMirrorError
      );
    }
    emitPaymentOperationsUpdate(
      restaurant,
      payload,
      options?.eventSource || "local"
    );
  } catch (error) {
    try {
      const emergencyPayload = payload.slice(0, 120).map((operation) =>
        buildEmergencyPaymentOperation(operation)
      );
      const serializedEmergencyPayload = JSON.stringify(emergencyPayload);

      try {
        window.sessionStorage.setItem(
          sessionStorageKey,
          serializedEmergencyPayload
        );
      } catch (sessionError) {
        console.warn(
          "No pudimos guardar las operaciones de pago en sessionStorage:",
          sessionError
        );
      }

      inMemoryPaymentOperationsStore.set(storageKey, emergencyPayload);
      emitPaymentOperationsUpdate(
        restaurant,
        emergencyPayload,
        options?.eventSource || "local"
      );
      return emergencyPayload;
    } catch (emergencyError) {
      console.error("No pudimos guardar las operaciones de pago:", emergencyError);
      inMemoryPaymentOperationsStore.set(storageKey, payload);
      emitPaymentOperationsUpdate(
        restaurant,
        payload,
        options?.eventSource || "local"
      );
      return payload;
    }
  }

  return payload;
};

export const savePaymentOperationCommissionSettlement = (
  restaurant,
  operationId,
  settlement = {}
) => {
  const normalizedOperationId = toSafeText(operationId, 160);

  if (!normalizedOperationId) {
    return getPaymentOperations(restaurant);
  }

  const currentOperations = getPaymentOperations(restaurant);
  const nextSettledAt = toSafeText(
    settlement?.approvedAt || settlement?.settledAt || new Date().toISOString(),
    80
  );
  const nextPaymentIntentId = toSafeText(settlement?.paymentIntentId, 120);
  const nextAppliedDebtAmount = roundMoney(settlement?.appliedDebtAmount);
  const nextGeneratedDebtAmount = roundMoney(settlement?.generatedDebtAmount);
  const nextOutstandingDebtAmount =
    settlement?.outstandingDebtAmount === null ||
    settlement?.outstandingDebtAmount === undefined
      ? null
      : roundMoney(settlement.outstandingDebtAmount);

  let changed = false;

  const nextOperations = currentOperations.map((operation) => {
    if (operation.id !== normalizedOperationId) {
      return operation;
    }

    const nextOperation = {
      ...operation,
      paymentIntentId: nextPaymentIntentId || operation.paymentIntentId || "",
      commissionLedgerSettledAt: nextSettledAt,
      commissionLedgerAppliedDebtAmount: nextAppliedDebtAmount,
      commissionLedgerGeneratedDebtAmount: nextGeneratedDebtAmount,
      commissionLedgerOutstandingDebtAmount: nextOutstandingDebtAmount,
    };

    if (
      nextOperation.paymentIntentId !== (operation.paymentIntentId || "") ||
      nextOperation.commissionLedgerSettledAt !==
        (operation.commissionLedgerSettledAt || "") ||
      nextOperation.commissionLedgerAppliedDebtAmount !==
        roundMoney(operation.commissionLedgerAppliedDebtAmount) ||
      nextOperation.commissionLedgerGeneratedDebtAmount !==
        roundMoney(operation.commissionLedgerGeneratedDebtAmount) ||
      nextOperation.commissionLedgerOutstandingDebtAmount !==
        (operation.commissionLedgerOutstandingDebtAmount === null ||
        operation.commissionLedgerOutstandingDebtAmount === undefined
          ? null
          : roundMoney(operation.commissionLedgerOutstandingDebtAmount))
    ) {
      changed = true;
    }

    return nextOperation;
  });

  if (!changed) {
    return currentOperations;
  }

  return savePaymentOperations(restaurant, nextOperations, {
    eventSource: "commission-ledger-settlement",
  });
};
